@let backgroundUrl =
  tenantInfo.loginBackground | assetUrl: AssetFileSize.original | async;
<img
  class="background-image"
  [src]="backgroundUrl ? backgroundUrl : 'assets/login-background.png'"
/>
<div class="login">
  @let logoUrl = tenantInfo.logo | assetUrl: AssetFileSize.size300 | async;
  <div class="login-logo-container">
    <img
      class="login-logo"
      [src]="logoUrl ? logoUrl : 'assets/logo-darkblue.png'"
    />
  </div>
  @if (tenantInfo.loginMessage) {
    <p class="login-message text-caption">{{ tenantInfo.loginMessage }}</p>
  }
  @switch (view) {
    @case ("SIGNIN_OPTIONS") {
      @for (provider of identityProviders; track $index) {
        @switch (provider.type) {
          @case ("microsoft") {
            <button
              mat-flat-button
              class="login-option login-option-microsoft"
              color="primary"
              (click)="onChooseMicrosoftLogin(provider)"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="21"
                height="21"
                viewBox="0 0 21 21"
              >
                <rect x="1" y="1" width="9" height="9" fill="#f25022" />
                <rect x="1" y="11" width="9" height="9" fill="#00a4ef" />
                <rect x="11" y="1" width="9" height="9" fill="#7fba00" />
                <rect x="11" y="11" width="9" height="9" fill="#ffb900" />
              </svg>
              {{ provider.label ?? "Sign in with Microsoft" }}
            </button>
          }
          @case ("saml") {
            <button
              mat-flat-button
              class="login-option login-option-saml"
              color="primary"
              (click)="onChooseSamlLogin(provider)"
            >
              <svg
                width="21"
                height="21"
                viewBox="0 0 16 16"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
              >
                <g fill="#C22E33">
                  <path
                    d="M7.754 2l.463.41c.343.304.687.607 1.026.915C11.44 5.32 13.3 7.565 14.7 10.149c.072.132.137.268.202.403l.098.203-.108.057-.081-.115-.21-.299-.147-.214c-1.019-1.479-2.04-2.96-3.442-4.145a6.563 6.563 0 00-1.393-.904c-1.014-.485-1.916-.291-2.69.505-.736.757-1.118 1.697-1.463 2.653-.045.123-.092.245-.139.367l-.082.215-.172-.055c.1-.348.192-.698.284-1.049.21-.795.42-1.59.712-2.356.31-.816.702-1.603 1.093-2.39.169-.341.338-.682.5-1.025h.092z"
                  />
                  <path
                    d="M8.448 11.822c-1.626.77-5.56 1.564-7.426 1.36C.717 11.576 3.71 4.05 5.18 2.91l-.095.218a4.638 4.638 0 01-.138.303l-.066.129c-.76 1.462-1.519 2.926-1.908 4.53a7.482 7.482 0 00-.228 1.689c-.01 1.34.824 2.252 2.217 2.309.67.027 1.347-.043 2.023-.114.294-.03.587-.061.88-.084.108-.008.214-.021.352-.039l.231-.028z"
                  />
                  <path
                    d="M3.825 14.781c-.445.034-.89.068-1.333.108 4.097.39 8.03-.277 11.91-1.644-1.265-2.23-2.97-3.991-4.952-5.522.026.098.084.169.141.239l.048.06c.17.226.348.448.527.67.409.509.818 1.018 1.126 1.578.778 1.42.356 2.648-1.168 3.296-1.002.427-2.097.718-3.18.892-1.03.164-2.075.243-3.119.323z"
                  />
                </g>
              </svg>

              {{ provider.label ?? "Sign in with SAML" }}
            </button>
          }
        }
      }
      <button
        mat-flat-button
        class="login-option login-option-email"
        color="primary"
        (click)="onChooseEmailLogin()"
      >
        <mat-icon>mail</mat-icon>Sign in with email
      </button>
    }
    @case ("EMAIL") {
      <form (submit)="(false)">
        <h6 class="login-header">Sign in with email</h6>
        <div class="login-form" [formGroup]="form">
          <mat-form-field class="login-form-field">
            <mat-label>Email</mat-label>
            <input
              matInput
              type="email"
              name="email"
              autocomplete="username"
              formControlName="email"
              autofocus
              data-testid="email-input"
            />
            @if (form.get("email").errors?.["doesNotExist"]) {
              <mat-error>No account exists for this email address</mat-error>
            }
          </mat-form-field>

          @if (emailVerified) {
            <mat-form-field class="login-form-field">
              <mat-label>Password</mat-label>
              <input
                #passwordInput
                matInput
                type="password"
                name="password"
                autocomplete="current-password"
                formControlName="password"
                autofocus
                data-testid="password-input"
              />
              @if (loginError === "INVALID_PASSWORD") {
                <mat-error
                  >The email and password you entered don't match</mat-error
                >
              }
            </mat-form-field>
          }

          <div class="login-form-buttons">
            @if (!emailVerified) {
              <button
                mat-flat-button
                type="submit"
                color="primary"
                (click)="onVerifyEmail()"
                [disabled]="form.get('email').invalid"
                data-testid="next-button"
              >
                NEXT
              </button>
            }
            @if (emailVerified) {
              <span
                class="login-forgot-password text-caption"
                (click)="onForgotPassword()"
                >Forgot password?</span
              >
              <button
                mat-flat-button
                type="submit"
                color="primary"
                (click)="onUsernamePasswordLogin()"
                [disabled]="form.invalid"
                data-testid="sign-in-button"
              >
                SIGN IN
              </button>
            }
          </div>
        </div>
      </form>
    }
    @case ("PASSWORD_FORGET") {
      <form (submit)="(false)">
        <h6 class="login-header">Recover password</h6>
        <p>
          Get instructions sent to this email that explain how to reset your
          password
        </p>
        <div class="login-form" [formGroup]="form">
          <mat-form-field class="login-form-field">
            <mat-label>Email</mat-label>
            <input
              matInput
              type="email"
              autocomplete="username"
              formControlName="email"
              autofocus
            />
            @if (form.get("email").invalid || loginError === "INVALID_EMAIL") {
              <mat-error>Not a valid email</mat-error>
            }
          </mat-form-field>

          <div class="login-form-buttons">
            @if (emailVerified) {
              <button
                mat-button
                color="primary"
                (click)="onCancelPasswordReset()"
              >
                CANCEL
              </button>
            }
            @if (isLoading) {
              <lib-spinner [size]="30"></lib-spinner>
            } @else {
              <button
                mat-flat-button
                color="primary"
                (click)="onSendPasswordReset()"
                [disabled]="form.get('email').invalid"
              >
                SEND
              </button>
            }
          </div>
        </div>
      </form>
    }
    @case ("PASSWORD_RESET") {
      <form (submit)="(false)">
        @if (loginError === "RESET_EXPIRED") {
          <h6 class="login-header">Try resetting your password again</h6>
          <p>
            Your request to reset your password has expired or the link has
            already been used
          </p>
          <div class="login-form-buttons">
            <button
              mat-flat-button
              color="primary"
              (click)="onCancelPasswordReset()"
            >
              CONTINUE
            </button>
          </div>
        } @else {
          <h6 class="login-header">Reset your password</h6>
          <p>
            for <b>{{ form.get("email").value }}</b>
          </p>
          <div class="login-form" [formGroup]="form">
            <mat-form-field class="login-form-field">
              <mat-label>New password</mat-label>
              <input
                #passwordInput
                matInput
                type="password"
                formControlName="password"
                autofocus
              />
            </mat-form-field>

            <div class="login-form-buttons">
              <button
                mat-flat-button
                color="primary"
                (click)="onConfirmPasswordReset()"
                [disabled]="form.get('password').invalid"
              >
                SAVE
              </button>
            </div>
          </div>
        }
      </form>
    }
    @case ("PASSWORD_RESET_COMPLETE") {
      <h6 class="login-header">Password reset successfully</h6>
      <a href="/">Click here to return to the login screen</a>
    }
  }

  <p class="login-footer text-caption">
    Need help?
    <a href="https://joinverify.com/knowledge-base/" target="_blank"
      >Visit our knowledge base</a
    >
  </p>
  <!-- <firebase-ui
                    *ngIf="isTenantSet"
                    (signInSuccessWithAuthResult)="successCallback()"
                    (signInFailure)="errorCallback($event)"
                    (uiShown)="uiShownCallback()"
                  ></firebase-ui> -->
</div>
